import { useEffect, useMemo, useState } from 'react';
import type { AssetDetailRecord, AssetKind } from '@shared/types';
import { Icon } from '@renderer/shared/components/icons';
import { TextAreaField, TextField, ValuePair } from '@renderer/shared/components/primitives';
import { useI18n } from '@renderer/shared/i18n';
import { getAssetPreviewUrl } from '@renderer/shared/utils/asset-url';
import { areDraftsEqual } from '@renderer/shared/utils/draft-equality';
import { getAssetById, updateAsset } from '../resources-repository';

function toAssetPreviewUrl(detail: AssetDetailRecord): string | null {
  const { asset } = detail;
  if (!asset.storagePath) {
    return null;
  }

  if (asset.kind !== 'image' && asset.kind !== 'icon' && asset.kind !== 'logo') {
    return null;
  }

  return getAssetPreviewUrl(asset.id);
}

function getAssetKindIcon(kind: AssetKind): Parameters<typeof Icon>[0]['name'] {
  switch (kind) {
    case 'image':
      return 'images';
    case 'pdf':
    case 'document':
      return 'document';
    case 'logo':
    case 'icon':
      return 'icon';
    default:
      return 'document';
  }
}

interface Draft {
  name: string;
  altText: string;
  description: string;
}

function toDraft(detail: AssetDetailRecord): Draft {
  return {
    name: detail.asset.name,
    altText: detail.asset.altText ?? '',
    description: detail.asset.description ?? ''
  };
}

export function AssetDetailsModal({
  assetId,
  isOpen,
  onClose,
  onSaved
}: {
  assetId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved: (detail: AssetDetailRecord) => void;
}): JSX.Element | null {
  const { locale, t } = useI18n();
  const [detail, setDetail] = useState<AssetDetailRecord | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        if (!isSaving) {
          onClose();
        }
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, isSaving, onClose]);

  useEffect(() => {
    if (!isOpen || !assetId) {
      setDetail(null);
      setDraft(null);
      setError(null);
      setIsLoading(false);
      setPreviewFailed(false);
      return;
    }

    let active = true;
    setIsLoading(true);
    setError(null);
    setPreviewFailed(false);

    void getAssetById(assetId)
      .then((nextDetail) => {
        if (!active) {
          return;
        }

        if (!nextDetail) {
          setError(t('resources.modal.notFound'));
          return;
        }

        setDetail(nextDetail);
        setDraft(toDraft(nextDetail));
      })
      .catch((nextError) => {
        if (active) {
          setError(nextError instanceof Error ? nextError.message : t('resources.modal.loadFailed'));
        }
      })
      .finally(() => {
        if (active) {
          setIsLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [assetId, isOpen, t]);

  const updatedLabel = useMemo(() => {
    if (!detail) {
      return '';
    }

    return new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(detail.asset.updatedAt));
  }, [detail, locale]);

  if (!isOpen) {
    return null;
  }

  const previewUrl = detail ? toAssetPreviewUrl(detail) : null;
  const isDirty = detail && draft ? !areDraftsEqual(draft, toDraft(detail)) : false;

  async function handleSave(): Promise<void> {
    if (!assetId || !draft) {
      return;
    }

    if (!draft.name.trim()) {
      setError(t('resources.modal.validationName'));
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const nextDetail = await updateAsset({
        id: assetId,
        name: draft.name.trim(),
        altText: draft.altText.trim() ? draft.altText.trim() : null,
        description: draft.description.trim() ? draft.description.trim() : null
      });

      setDetail(nextDetail);
      setDraft(toDraft(nextDetail));
      onSaved(nextDetail);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : t('resources.modal.saveFailed'));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div
      className="av-asset-modal__overlay"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          if (!isSaving) {
            onClose();
          }
        }
      }}
    >
      <div aria-modal="true" className="av-asset-modal" role="dialog" onClick={(event) => event.stopPropagation()}>
        <div className="av-asset-modal__header">
          <h2>{detail?.asset.name ?? t('resources.modal.title')}</h2>
          <button aria-label={t('common.close')} className="av-asset-modal__close" type="button" onClick={onClose} disabled={isSaving}>
            <Icon name="times" />
          </button>
        </div>

        {error ? <div className="av-inline-error">{error}</div> : null}

        <div className="av-asset-modal__body">
          <div className="av-asset-modal__preview">
            {previewUrl && !previewFailed ? (
              <img
                alt={draft?.altText ?? ''}
                src={previewUrl}
                onLoad={() => setPreviewFailed(false)}
                onError={() => setPreviewFailed(true)}
              />
            ) : (
              <div className="av-asset-modal__preview-fallback" aria-hidden>
                <Icon name={detail ? getAssetKindIcon(detail.asset.kind) : 'document'} />
              </div>
            )}
          </div>

          <div className="av-asset-modal__form">
            {detail ? (
              <div className="av-asset-modal__meta">
                <ValuePair label={t('quickAdd.fields.category')} value={t(`enums.assetKind.${detail.asset.kind}`)} />
                <ValuePair label={t('quickAdd.fields.mimeType')} value={detail.asset.mimeType ?? '—'} />
                <ValuePair label={t('resources.modal.updatedAt')} value={updatedLabel || '—'} />
              </div>
            ) : null}

            <label className="av-form-field">
              <span>{t('quickAdd.fields.name')}</span>
              <TextField
                value={draft?.name ?? ''}
                disabled={!draft || isLoading || isSaving}
                onValueChange={(value) => setDraft((current) => (current ? { ...current, name: value } : current))}
              />
            </label>

            <label className="av-form-field">
              <span>{t('resources.modal.fields.altText')}</span>
              <TextField
                value={draft?.altText ?? ''}
                disabled={!draft || isLoading || isSaving}
                onValueChange={(value) => setDraft((current) => (current ? { ...current, altText: value } : current))}
              />
            </label>

            <label className="av-form-field">
              <span>{t('resources.modal.fields.description')}</span>
              <TextAreaField
                className="av-textarea"
                rows={4}
                value={draft?.description ?? ''}
                disabled={!draft || isLoading || isSaving}
                onValueChange={(value) => setDraft((current) => (current ? { ...current, description: value } : current))}
              />
            </label>
          </div>
        </div>

        <div className="av-asset-modal__footer">
          <button className="av-secondary-button" type="button" onClick={onClose} disabled={isSaving}>
            {t('common.close')}
          </button>
          <button className="av-primary-button" type="button" onClick={() => void handleSave()} disabled={!isDirty || isSaving || isLoading}>
            {t('common.applyChanges')}
          </button>
        </div>
      </div>
    </div>
  );
}
